import React, { useState, useEffect } from 'react';
import './Financeiro.css';
import EstadoVazio from './EstadoVazio';
import { apiFetch, ApiError } from './api';

function Financeiro() {
  // Estados para gerenciamento dos dados financeiros
  const [pagamentos, setPagamentos] = useState([]);
  const [despesas, setDespesas] = useState([]);
  const [contasBancarias, setContasBancarias] = useState([]);
  const [resumoFinanceiro, setResumoFinanceiro] = useState({
    totalReceitas: 0,
    totalDespesas: 0,
    saldoLiquido: 0,
    receitasMes: 0,
    despesasMes: 0,
    saldoMes: 0,
    contasReceber: 0,
    contasPagar: 0
  });
  
  // Estados para navegação mensal
  const [mesSelecionado, setMesSelecionado] = useState(new Date().getMonth());
  const [anoSelecionado, setAnoSelecionado] = useState(new Date().getFullYear());
  
  const [modalAberto, setModalAberto] = useState(false);
  const [modalTipo, setModalTipo] = useState('');
  const [formValues, setFormValues] = useState({
    descricao: '',
    valor: '',
    data: '',
    cliente: '',
    categoria: '',
    formaPagamento: 'pix',
    status: 'pendente',
    observacoes: '',
    contaBancaria: ''
  });
  const [modoEdicao, setModoEdicao] = useState(false);
  const [transacaoEditando, setTransacaoEditando] = useState(null);
  const [exibirAtrasados, setExibirAtrasados] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [recarregar, setRecarregar] = useState(0);
  const [salvando, setSalvando] = useState(false);
  const [erroModal, setErroModal] = useState(null);
  const [erroConta, setErroConta] = useState(null);
  const [modalContaAberto, setModalContaAberto] = useState(false);
  const [formContaValues, setFormContaValues] = useState({
    nome: '',
    banco: '',
    agencia: '',
    conta: '',
    tipo: 'corrente',
    saldoInicial: ''
  });
  
  // Dados simulados para clientes
  const clientes = [
    { id: 1, nome: 'Padaria Delícia Diária' },
    { id: 2, nome: 'Soluções Tech Inovare' },
    { id: 3, nome: 'Bella Moda Fashion' },
    { id: 4, nome: 'Natura Cosméticos Artesanais' },
    { id: 5, nome: 'Mãos à Obra Ferramentas' }
  ];
  
  // Categorias de despesas
  const categoriasDespesa = [
    'Aluguel',
    'Salários',
    'Serviços de Terceiros', 
    'Marketing',
    'Taxas e Impostos',
    'Materiais de Escritório',
    'Software e Tecnologia',
    'Viagens e Deslocamentos',
    'Outros'
  ];
  
  // Nome dos meses em português
  const nomesMeses = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];
  
  // Dados simulados para contas bancárias
  const bancos = [
    'Banco do Brasil',
    'Caixa Econômica',
    'Itaú',
    'Bradesco',
    'Santander',
    'Banco Inter',
    'Nubank',
    'Outro'
  ];
  
  // Inicializar dados
  useEffect(() => {
    let ativo = true;
    setCarregando(true);
    setErro(null);
    const mes = mesSelecionado + 1; // a API espera mês 1-12; o estado é 0-11 (Date.getMonth)
    Promise.all([
      apiFetch(`/api/financeiro/transacoes?mes=${mes}&ano=${anoSelecionado}`),
      apiFetch('/api/financeiro/contas-bancarias'),
    ])
      .then(([transacoes, contas]) => {
        if (!ativo) {
          return;
        }
        // mapeia o shape da API para o shape que a tela já consome
        const pagamentosUI = (transacoes.pagamentos || []).map((p) => ({
          id: p.id,
          cliente_id: p.clienteId,
          cliente_nome: p.clienteNome,
          descricao: p.descricao,
          valor: p.valor,
          data: p.dataPagamento || p.dataVencimento,
          status: p.status,
          formaPagamento: p.formaPagamento,
          contaBancariaId: p.contaBancariaId,
          observacoes: p.observacoes,
        }));
        const despesasUI = (transacoes.despesas || []).map((d) => ({
          id: d.id,
          descricao: d.descricao,
          valor: d.valor,
          data: d.data,
          categoria: d.categoria,
          status: d.status,
          contaBancariaId: d.contaBancariaId,
          observacoes: d.observacoes,
        }));
        setPagamentos(pagamentosUI);
        setDespesas(despesasUI);
        setContasBancarias((contas.contas || []).map((c) => ({
          id: c.id,
          nome: c.nome,
          banco: c.banco,
          agencia: c.agencia,
          conta: c.conta,
          saldoInicial: c.saldoInicial,
          saldoAtual: c.saldoAtual,
        })));
        // resumo vem do backend (transacoes.totais) — sem recalcular no front, evita drift (review Leo #56)
        const t = transacoes.totais || {};
        setResumoFinanceiro({
          totalReceitas: t.receita || 0,
          totalDespesas: t.despesa || 0,
          saldoLiquido: t.saldo || 0,
          receitasMes: t.receita || 0,
          despesasMes: t.despesa || 0,
          saldoMes: t.saldo || 0,
          contasReceber: t.contasReceber || 0,
          contasPagar: t.contasPagar || 0,
        });
      })
      .catch(() => {
        if (ativo) {
          setErro('Não foi possível carregar os dados financeiros. Tente novamente.');
        }
      })
      .finally(() => {
        if (ativo) {
          setCarregando(false);
        }
      });
    return () => {
      ativo = false;
    };
  }, [mesSelecionado, anoSelecionado, recarregar]);

  
  // Funções para navegação entre meses
  const mesAnterior = () => {
    if (mesSelecionado === 0) {
      setMesSelecionado(11);
      setAnoSelecionado(anoSelecionado - 1);
    } else {
      setMesSelecionado(mesSelecionado - 1);
    }
  };
  
  const proximoMes = () => {
    if (mesSelecionado === 11) {
      setMesSelecionado(0);
      setAnoSelecionado(anoSelecionado + 1);
    } else {
      setMesSelecionado(mesSelecionado + 1);
    }
  };
  
  const irParaMesAtual = () => {
    const dataAtual = new Date();
    setMesSelecionado(dataAtual.getMonth());
    setAnoSelecionado(dataAtual.getFullYear());
  };
  // Função para filtrar transações do mês atual
  const filtrarTransacoesMes = (lista) => {
    return lista.filter(item => {
      const dataItem = new Date(item.data);
      return dataItem.getMonth() === mesSelecionado && dataItem.getFullYear() === anoSelecionado;
    });
  };
  
  // Função para abrir o modal
  const abrirModal = (tipo, transacao = null) => {
    setModalTipo(tipo);
    setModalAberto(true);
    setErroModal(null);

    if (transacao) {
      // Modo edição: contaBancariaId vem no payload de /transacoes (receita e despesa) — pré-preenche a conta.
      setModoEdicao(true);
      setTransacaoEditando(transacao);
      const comum = {
        descricao: transacao.descricao,
        valor: transacao.valor != null ? transacao.valor.toString() : '',
        data: transacao.data,
        status: transacao.status,
        observacoes: transacao.observacoes || '',
        contaBancaria: transacao.contaBancariaId != null ? transacao.contaBancariaId.toString() : '',
      };
      if (tipo === 'receita') {
        setFormValues({
          ...comum,
          cliente: transacao.cliente_id != null ? transacao.cliente_id.toString() : '',
          categoria: '',
          formaPagamento: transacao.formaPagamento || 'pix',
        });
      } else {
        setFormValues({ ...comum, cliente: '', categoria: transacao.categoria, formaPagamento: 'pix' });
      }
    } else {
      // Modo adição
      setModoEdicao(false);
      setTransacaoEditando(null);

      // Pré-preencher a data com o mês atual
      const dataAtualStr = `${anoSelecionado}-${(mesSelecionado + 1).toString().padStart(2, '0')}-${new Date().getDate().toString().padStart(2, '0')}`;

      setFormValues({
        descricao: '',
        valor: '',
        data: dataAtualStr,
        cliente: '',
        categoria: '',
        formaPagamento: 'pix',
        status: 'pendente',
        observacoes: '',
        contaBancaria: ''
      });
    }
  };

  // Função para fechar o modal
  const fecharModal = () => {
    setModalAberto(false);
    setErroModal(null);
  };
  
  // Função para lidar com as mudanças no formulário
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value
    });
  };
  
  // Função para abrir modal de conta bancária
  const abrirModalConta = () => {
    setModalContaAberto(true);
    setErroConta(null);
    setFormContaValues({
      nome: '',
      banco: '',
      agencia: '',
      conta: '',
      tipo: 'corrente',
      saldoInicial: ''
    });
  };

  // Função para fechar modal de conta bancária
  const fecharModalConta = () => {
    setModalContaAberto(false);
    setErroConta(null);
  };
  
  // Função para lidar com mudanças no formulário de conta
  const handleContaFormChange = (e) => {
    const { name, value } = e.target;
    setFormContaValues({
      ...formContaValues,
      [name]: value
    });
  };
  
  // Cria a conta bancária no backend (POST /contas-bancarias) e recarrega.
  const salvarContaBancaria = async () => {
    if (salvando) {
      return;
    }
    if (!formContaValues.nome || !formContaValues.banco || !formContaValues.saldoInicial) {
      setErroConta('Preencha nome, banco e saldo inicial.');
      return;
    }
    setSalvando(true);
    setErroConta(null);
    try {
      await apiFetch('/api/financeiro/contas-bancarias', {
        method: 'POST',
        body: {
          nome: formContaValues.nome,
          banco: formContaValues.banco,
          agencia: formContaValues.agencia,
          conta: formContaValues.conta,
          tipo: formContaValues.tipo,
          saldoInicial: parseFloat(formContaValues.saldoInicial),
        },
      });
      fecharModalConta();
      setRecarregar((r) => r + 1);
    } catch (e) {
      setErroConta(e instanceof ApiError && e.status === 400
        ? 'Verifique os campos da conta (saldo inicial inválido?).'
        : 'Não foi possível salvar a conta. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  };
  
  // Persiste o registro no backend: cria via POST /transacoes (unificado) ou edita via PUT específico.
  const salvarRegistro = async () => {
    if (salvando) {
      return;
    }
    if (!formValues.descricao || !formValues.valor || !formValues.data || !formValues.contaBancaria) {
      setErroModal('Preencha os campos obrigatórios (descrição, valor, data e conta).');
      return;
    }
    if (modalTipo === 'receita' && !formValues.cliente) {
      setErroModal('Selecione o cliente da receita.');
      return;
    }
    if (modalTipo === 'despesa' && !formValues.categoria) {
      setErroModal('Selecione a categoria da despesa.');
      return;
    }

    const valor = parseFloat(formValues.valor);
    const pago = formValues.status === 'pago';
    const contaBancariaId = formValues.contaBancaria;
    setSalvando(true);
    setErroModal(null);
    try {
      if (modalTipo === 'receita') {
        // 1 parcela por padrão; quitada se status=pago. status do pagamento é derivado no backend.
        const corpo = {
          descricao: formValues.descricao,
          valor,
          parcelas: 1,
          parcelasPagas: pago ? 1 : 0,
          formaPagamento: formValues.formaPagamento,
          dataPagamento: pago ? formValues.data : null,
          dataVencimento: pago ? null : formValues.data,
          observacoes: formValues.observacoes,
          contaBancariaId,
        };
        if (modoEdicao) {
          await apiFetch(`/api/clientes/${transacaoEditando.cliente_id}/pagamentos/${transacaoEditando.id}`,
            { method: 'PUT', body: corpo });
        } else {
          await apiFetch('/api/financeiro/transacoes',
            { method: 'POST', body: { tipo: 'receita', clienteId: formValues.cliente, ...corpo } });
        }
      } else {
        const corpo = {
          descricao: formValues.descricao,
          valor,
          data: formValues.data,
          categoria: formValues.categoria,
          status: formValues.status,
          observacoes: formValues.observacoes,
          contaBancariaId,
        };
        if (modoEdicao) {
          await apiFetch(`/api/financeiro/despesas/${transacaoEditando.id}`, { method: 'PUT', body: corpo });
        } else {
          await apiFetch('/api/financeiro/transacoes', { method: 'POST', body: { tipo: 'despesa', ...corpo } });
        }
      }
      fecharModal();
      setRecarregar((r) => r + 1); // recarrega do backend (saldo/totais reais)
    } catch (e) {
      if (e instanceof ApiError && e.status === 400) {
        setErroModal('Verifique os campos: algum valor é inválido para o registro.');
      } else if (e instanceof ApiError && e.status === 404) {
        setErroModal('Cliente ou conta não encontrados neste escritório.');
      } else {
        setErroModal('Não foi possível salvar. Tente novamente.');
      }
    } finally {
      setSalvando(false);
    }
  };
  
  // Formatar valor em moeda brasileira
  const formatarMoeda = (valor) => {
    return valor.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };
  
  // Função para verificar se uma transação está atrasada
  const isTransacaoAtrasada = (transacao) => {
    if (transacao.status !== 'pendente') return false;
    
    const dataTransacao = new Date(transacao.data);
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    
    return dataTransacao < hoje;
  };
  
  // Função para filtrar transações atrasadas
  const filtrarTransacoesAtrasadas = () => {
    const pagamentosAtrasados = pagamentos.filter(isTransacaoAtrasada);
    const despesasAtrasadas = despesas.filter(isTransacaoAtrasada);
    return [...pagamentosAtrasados, ...despesasAtrasadas].sort((a, b) => new Date(a.data) - new Date(b.data));
  };

  return (
    <div className="financeiro-container">
      <div className="financeiro-header">
        <h1>Controle Financeiro</h1>
        <p className="descricao">Gerencie receitas e despesas do seu escritório</p>
      </div>

      {erro && <p className="financeiro-erro" role="alert">{erro}</p>}
      {carregando && <p className="financeiro-carregando">Carregando dados financeiros...</p>}

      {/* Navegação Mensal */}
      <div className="navegacao-mensal">
        <button className="botao-mes" onClick={mesAnterior}>
          <i className="fas fa-chevron-left"></i>
        </button>
        <div className="mes-atual">
          <h2>{nomesMeses[mesSelecionado]} de {anoSelecionado}</h2>
          {(mesSelecionado !== new Date().getMonth() || anoSelecionado !== new Date().getFullYear()) && (
            <button className="botao-hoje" onClick={irParaMesAtual}>
              Mês Atual
            </button>
          )}
        </div>
        <button className="botao-mes" onClick={proximoMes}>
          <i className="fas fa-chevron-right"></i>
        </button>
      </div>
      
      {/* Resumo Financeiro do Mês */}
      <div className="resumo-mensal">
        <div className="cartao-resumo-mensal">
          <div className="resumo-cabecalho">
            <h3>Resumo do Mês</h3>
          </div>
          <div className="resumo-corpo">
            <div className="resumo-valores">
              <div className="resumo-item receitas">
                <span className="rotulo">Receitas</span>
                <span className="valor">{formatarMoeda(resumoFinanceiro.receitasMes)}</span>
              </div>
              <div className="resumo-item despesas">
                <span className="rotulo">Despesas</span>
                <span className="valor">{formatarMoeda(resumoFinanceiro.despesasMes)}</span>
              </div>
              <div className="resumo-item saldo">
                <span className="rotulo">Saldo</span>
                <span className={`valor ${resumoFinanceiro.saldoMes >= 0 ? 'positivo' : 'negativo'}`}>
                  {formatarMoeda(resumoFinanceiro.saldoMes)}
                </span>
              </div>
            </div>
            
            <div className="resumo-grafico">
              <div className="grafico-barras">
                <div 
                  className="barra receitas-barra" 
                  style={{ 
                    width: resumoFinanceiro.receitasMes > 0 
                      ? `${Math.min((resumoFinanceiro.receitasMes / (resumoFinanceiro.receitasMes + resumoFinanceiro.despesasMes)) * 100, 70)}%` 
                      : '0%' 
                  }}
                ></div>
                <div 
                  className="barra despesas-barra" 
                  style={{ 
                    width: resumoFinanceiro.despesasMes > 0 
                      ? `${Math.min((resumoFinanceiro.despesasMes / (resumoFinanceiro.receitasMes + resumoFinanceiro.despesasMes)) * 100, 70)}%` 
                      : '0%' 
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="pendencias-resumo">
          <div className="pendencia-card">
            <div className="pendencia-icone pendencias-receber">
              <i className="fas fa-arrow-down"></i>
            </div>
            <div className="pendencia-conteudo">
              <span className="pendencia-titulo">A Receber</span>
              <span className="pendencia-valor">
                {formatarMoeda(resumoFinanceiro.contasReceber)}
              </span>
            </div>
          </div>
          
          <div className="pendencia-card">
            <div className="pendencia-icone pendencias-pagar">
              <i className="fas fa-arrow-up"></i>
            </div>
            <div className="pendencia-conteudo">
              <span className="pendencia-titulo">A Pagar</span>
              <span className="pendencia-valor">
                {formatarMoeda(resumoFinanceiro.contasPagar)}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Transações Atrasadas */}
      <div className="secao-atrasados">
        <div className="atrasados-cabecalho" onClick={() => setExibirAtrasados(!exibirAtrasados)}>
          <h3>
            <i className={`fas fa-exclamation-triangle alerta-icone`}></i>
            Transações Atrasadas
            <span className="contador-atrasados">{filtrarTransacoesAtrasadas().length}</span>
            <i className={`fas ${exibirAtrasados ? 'fa-chevron-up' : 'fa-chevron-down'} toggle-icone`}></i>
          </h3>
        </div>
        
        {exibirAtrasados && (
          <div className="atrasados-conteudo">
            {filtrarTransacoesAtrasadas().length > 0 ? (
              filtrarTransacoesAtrasadas().map(transacao => {
                const isPagamento = 'cliente_nome' in transacao;
                return (
                  <div key={`atrasado-${isPagamento ? 'p' : 'd'}-${transacao.id}`} 
                    className={`transacao-atrasada ${isPagamento ? 'receita' : 'despesa'}`}
                  >
                    <div className="transacao-data">
                      <span className="dia">{new Date(transacao.data).getDate()}</span>
                      <span className="mes">{nomesMeses[new Date(transacao.data).getMonth()].substring(0, 3)}</span>
                    </div>
                    
                    <div className="transacao-detalhes">
                      <div className="transacao-info">
                        <span className="transacao-descricao">{transacao.descricao}</span>
                        <span className="transacao-categoria">
                          {isPagamento ? transacao.cliente_nome : transacao.categoria}
                        </span>
                      </div>
                      
                      <div className="transacao-valor">
                        <span className={`valor ${isPagamento ? 'positivo' : 'negativo'}`}>
                          {isPagamento ? '+' : '-'} {formatarMoeda(transacao.valor)}
                        </span>
                        <div className="transacao-acoes">
                          <button 
                            className="btn-editar" 
                            onClick={(e) => {
                              e.stopPropagation();
                              abrirModal(isPagamento ? 'receita' : 'despesa', transacao);
                            }}
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="sem-atrasados">
                <p>Não há transações atrasadas.</p>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Botões de Ação */}
      <div className="acoes-rapidas">
        <button className="botao-adicionar conta" onClick={abrirModalConta}>
          <i className="fas fa-university"></i> Adicionar Conta Bancária
        </button>
        <button className="botao-adicionar receita" onClick={() => abrirModal('receita')}>
          <i className="fas fa-plus-circle"></i> Adicionar Receita
        </button>
        <button className="botao-adicionar despesa" onClick={() => abrirModal('despesa')}>
          <i className="fas fa-minus-circle"></i> Adicionar Despesa
        </button>
      </div>
      
      {/* Lista de Contas Bancárias */}
      <div className="contas-bancarias-container">
        <h3>Contas Bancárias</h3>
        <div className="contas-grid">
          {contasBancarias.map(conta => (
            <div key={conta.id} className="conta-card">
              <div className="conta-header">
                <h4>{conta.nome}</h4>
                <span className="banco-nome">{conta.banco}</span>
              </div>
              <div className="conta-info">
                <p>Agência: {conta.agencia}</p>
                <p>Conta: {conta.conta}</p>
                <p className="saldo-atual">
                  Saldo Atual: {formatarMoeda(conta.saldoAtual)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Transações do Mês */}
      <div className="transacoes-container">
        <div className="transacoes-cabecalho">
          <h3>Transações de {nomesMeses[mesSelecionado]}</h3>
        </div>
        
        {/* Lista de Transações */}
        <div className="lista-transacoes">
          {filtrarTransacoesMes([...pagamentos, ...despesas])
            .sort((a, b) => new Date(a.data) - new Date(b.data))
            .map(transacao => {
              const isPagamento = 'cliente_nome' in transacao;
              return (
                <div key={`${isPagamento ? 'p' : 'd'}-${transacao.id}`} 
                  className={`transacao-item ${transacao.status === 'pendente' ? 'pendente' : ''} ${isPagamento ? 'receita' : 'despesa'}`}
                >
                  <div className="transacao-data">
                    <span className="dia">{new Date(transacao.data).getDate()}</span>
                    <span className="mes">{nomesMeses[new Date(transacao.data).getMonth()].substring(0, 3)}</span>
                  </div>
                  
                  <div className="transacao-detalhes">
                    <div className="transacao-info">
                      <span className="transacao-descricao">{transacao.descricao}</span>
                      <span className="transacao-categoria">
                        {isPagamento ? transacao.cliente_nome : transacao.categoria}
                      </span>
                    </div>
                    
                    <div className="transacao-valor">
                      <span className={`valor ${isPagamento ? 'positivo' : 'negativo'}`}>
                        {isPagamento ? '+' : '-'} {formatarMoeda(transacao.valor)}
                      </span>
                      <div className="acoes-transacao">
                        <span className={`status-badge ${transacao.status}`}>
                          {transacao.status === 'pago' ? 'Pago' : 'Pendente'}
                        </span>
                        <button 
                          className="btn-editar" 
                          onClick={(e) => {
                            e.stopPropagation();
                            abrirModal(isPagamento ? 'receita' : 'despesa', transacao);
                          }}
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            
          {filtrarTransacoesMes([...pagamentos, ...despesas]).length === 0 && (
            <EstadoVazio
              icone="fa-solid fa-calendar-day"
              titulo="Nenhuma transação no período"
              descricao={`Nenhuma transação encontrada para ${nomesMeses[mesSelecionado]} de ${anoSelecionado}.`}
              acao={{ rotulo: 'Adicionar Transação', onClick: () => abrirModal('receita') }}
            />
          )}
        </div>
      </div>
      
      {/* Modal de Adição/Edição */}
      {modalAberto && (
        <div className="modal-overlay" onClick={fecharModal}>
          <div className="modal-conteudo" onClick={(e) => e.stopPropagation()}>
            <div className="modal-cabecalho">
              <h2>
                {modoEdicao ? 'Editar' : 'Adicionar'} {modalTipo === 'receita' ? 'Receita' : 'Despesa'}
              </h2>
              <button className="modal-fechar" onClick={fecharModal}>×</button>
            </div>
            
            <div className="modal-corpo">
              <div className="form-grupo">
                <label htmlFor="descricao">Descrição*</label>
                <input 
                  type="text" 
                  id="descricao" 
                  name="descricao" 
                  value={formValues.descricao}
                  onChange={handleFormChange}
                  required
                />
              </div>
              
              <div className="form-grupo">
                <label htmlFor="valor">Valor (R$)*</label>
                <input 
                  type="number" 
                  id="valor" 
                  name="valor" 
                  min="0" 
                  step="0.01" 
                  value={formValues.valor}
                  onChange={handleFormChange}
                  required
                />
              </div>
              
              <div className="form-grupo">
                <label htmlFor="data">Data*</label>
                <input 
                  type="date" 
                  id="data" 
                  name="data" 
                  value={formValues.data}
                  onChange={handleFormChange}
                  required
                />
              </div>
              
              {modalTipo === 'receita' ? (
                <div className="form-grupo">
                  <label htmlFor="cliente">Cliente*</label>
                  <select 
                    id="cliente" 
                    name="cliente" 
                    value={formValues.cliente}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="">Selecione um cliente</option>
                    {clientes.map(cliente => (
                      <option key={cliente.id} value={cliente.id}>
                        {cliente.nome}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="form-grupo">
                  <label htmlFor="categoria">Categoria*</label>
                  <select 
                    id="categoria" 
                    name="categoria" 
                    value={formValues.categoria}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="">Selecione uma categoria</option>
                    {categoriasDespesa.map((categoria, index) => (
                      <option key={index} value={categoria}>
                        {categoria}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className="form-grupo">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  name="status"
                  value={formValues.status}
                  onChange={handleFormChange}
                >
                  <option value="pendente">Pendente</option>
                  <option value="pago">Pago</option>
                </select>
              </div>

              {modalTipo === 'receita' && (
                <div className="form-grupo">
                  <label htmlFor="formaPagamento">Forma de Pagamento</label>
                  <select
                    id="formaPagamento"
                    name="formaPagamento"
                    value={formValues.formaPagamento}
                    onChange={handleFormChange}
                  >
                    <option value="pix">Pix</option>
                    <option value="boleto">Boleto</option>
                    <option value="cartao">Cartão</option>
                    <option value="transferencia">Transferência</option>
                    <option value="dinheiro">Dinheiro</option>
                  </select>
                </div>
              )}

              <div className="form-grupo">
                <label htmlFor="observacoes">Observações</label>
                <textarea 
                  id="observacoes" 
                  name="observacoes" 
                  rows="3" 
                  value={formValues.observacoes}
                  onChange={handleFormChange}
                ></textarea>
              </div>
              
              <div className="form-grupo">
                <label htmlFor="contaBancaria">Conta Bancária*</label>
                <select 
                  id="contaBancaria" 
                  name="contaBancaria" 
                  value={formValues.contaBancaria}
                  onChange={handleFormChange}
                  required
                >
                  <option value="">Selecione a conta</option>
                  {contasBancarias.map(conta => (
                    <option key={conta.id} value={conta.id}>
                      {conta.nome} - {conta.banco}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            {erroModal && <p className="financeiro-erro" role="alert">{erroModal}</p>}

            <div className="modal-rodape">
              <button className="botao-cancelar" onClick={fecharModal} disabled={salvando}>
                Cancelar
              </button>
              <button className="botao-salvar" onClick={salvarRegistro} disabled={salvando}>
                {salvando ? 'Salvando...' : (modoEdicao ? 'Atualizar' : 'Salvar')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Conta Bancária */}
      {modalContaAberto && (
        <div className="modal-overlay" onClick={fecharModalConta}>
          <div className="modal-conteudo" onClick={(e) => e.stopPropagation()}>
            <div className="modal-cabecalho">
              <h2>Adicionar Conta Bancária</h2>
              <button className="modal-fechar" onClick={fecharModalConta}>×</button>
            </div>
            
            <div className="modal-corpo">
              <div className="form-grupo">
                <label htmlFor="nome">Nome da Conta*</label>
                <input 
                  type="text" 
                  id="nome" 
                  name="nome" 
                  value={formContaValues.nome}
                  onChange={handleContaFormChange}
                  required
                />
              </div>
              
              <div className="form-grupo">
                <label htmlFor="banco">Banco*</label>
                <select 
                  id="banco" 
                  name="banco" 
                  value={formContaValues.banco}
                  onChange={handleContaFormChange}
                  required
                >
                  <option value="">Selecione o banco</option>
                  {bancos.map(banco => (
                    <option key={banco} value={banco}>
                      {banco}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-grupo">
                <label htmlFor="agencia">Agência*</label>
                <input 
                  type="text" 
                  id="agencia" 
                  name="agencia" 
                  value={formContaValues.agencia}
                  onChange={handleContaFormChange}
                  required
                />
              </div>
              
              <div className="form-grupo">
                <label htmlFor="conta">Número da Conta*</label>
                <input 
                  type="text" 
                  id="conta" 
                  name="conta" 
                  value={formContaValues.conta}
                  onChange={handleContaFormChange}
                  required
                />
              </div>
              
              <div className="form-grupo">
                <label htmlFor="tipo">Tipo*</label>
                <select
                  id="tipo"
                  name="tipo"
                  value={formContaValues.tipo}
                  onChange={handleContaFormChange}
                  required
                >
                  <option value="corrente">Corrente</option>
                  <option value="poupanca">Poupança</option>
                  <option value="investimento">Investimento</option>
                </select>
              </div>

              <div className="form-grupo">
                <label htmlFor="saldoInicial">Saldo Inicial*</label>
                <input
                  type="number"
                  id="saldoInicial"
                  name="saldoInicial"
                  min="0"
                  step="0.01"
                  value={formContaValues.saldoInicial}
                  onChange={handleContaFormChange}
                  required
                />
              </div>
            </div>

            {erroConta && <p className="financeiro-erro" role="alert">{erroConta}</p>}

            <div className="modal-rodape">
              <button className="botao-cancelar" onClick={fecharModalConta} disabled={salvando}>
                Cancelar
              </button>
              <button className="botao-salvar" onClick={salvarContaBancaria} disabled={salvando}>
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Financeiro; 